/* eslint-disable max-lines */
import { createContext, useCallback, useMemo, useState } from 'react';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';

export const PrescriptionReviewPayloadContext = createContext({});

const PrescriptionReviewPayloadProvider = ({ children }) => {
  const [payload, setPayload] = useState({});

  const [prescriptionHistory, setPrescriptionHistory] = useState([]);
  const [prescriptionHistoryIndex, setPrescriptionHistoryIndex] = useState(-1);
  const [fillHistory, setFillHistory] = useState([]);
  const [fillHistoryIndex, setFillHistoryIndex] = useState(-1);

  const savePrescriptionChanges = useCallback(
    (prescriptionDetails) => {
      setPayload((prevPayload) => {
        const currentPrescriptionDetails = prevPayload?.prescriptionDetails || {};
        const currentPrescriptionOnly = {
          prescriberName: currentPrescriptionDetails.prescriberName,
          refills: currentPrescriptionDetails.refills,
          prescribedQuantity: currentPrescriptionDetails.prescribedQuantity,
        };

        const newPrescriptionDetails = {
          ...currentPrescriptionOnly,
          ...prescriptionDetails,
        };

        setPrescriptionHistory((prevHistory) => {
          const truncatedHistory =
            prescriptionHistoryIndex < prevHistory.length - 1
              ? prevHistory.slice(0, prescriptionHistoryIndex + 1)
              : prevHistory;

          const newHistory = [
            ...truncatedHistory,
            JSON.parse(JSON.stringify(newPrescriptionDetails)),
          ];
          const limitedHistory = newHistory.slice(-50);
          const newIndex = limitedHistory.length - 1;
          setPrescriptionHistoryIndex(newIndex);

          return limitedHistory;
        });

        return {
          ...prevPayload,
          prescriptionDetails: {
            ...prevPayload?.prescriptionDetails,
            ...prescriptionDetails,
          },
        };
      });
    },
    [prescriptionHistoryIndex]
  );

  const saveFillChanges = useCallback(
    (fillDetails) => {
      setPayload((prevPayload) => {
        const currentDispensedQuantity = prevPayload?.prescriptionDetails?.dispensedQuantity;
        const currentState =
          currentDispensedQuantity !== undefined
            ? { dispensedQuantity: currentDispensedQuantity }
            : {};

        const newState = { ...currentState, ...fillDetails };

        setFillHistory((prevHistory) => {
          const truncatedHistory =
            fillHistoryIndex < prevHistory.length - 1
              ? prevHistory.slice(0, fillHistoryIndex + 1)
              : prevHistory;

          const newHistory = [...truncatedHistory, JSON.parse(JSON.stringify(newState))];
          const limitedHistory = newHistory.slice(-50);
          const newIndex = limitedHistory.length - 1;
          setFillHistoryIndex(newIndex);

          return limitedHistory;
        });

        return {
          ...prevPayload,
          prescriptionDetails: {
            ...prevPayload?.prescriptionDetails,
            ...fillDetails,
          },
        };
      });
    },
    [fillHistoryIndex]
  );

  const updatePayload = useCallback((newPayload) => {
    setPayload(newPayload);
  }, []);

  const undoPrescription = useCallback(() => {
    if (prescriptionHistoryIndex >= 0) {
      const previousIndex = prescriptionHistoryIndex - 1;
      let previousState = null;

      if (previousIndex < 0) {
        previousState = {};
        setPrescriptionHistoryIndex(-1);
      } else {
        previousState = prescriptionHistory[previousIndex];
        setPrescriptionHistoryIndex(previousIndex);
      }

      setPayload((prevPayload) => {
        const updatedPrescriptionDetails = { ...prevPayload?.prescriptionDetails };

        if (!previousState || Object.keys(previousState).length === 0) {
          delete updatedPrescriptionDetails.prescriberName;
          delete updatedPrescriptionDetails.refills;
          delete updatedPrescriptionDetails.prescribedQuantity;
        } else {
          if (previousState.prescriberName !== undefined) {
            updatedPrescriptionDetails.prescriberName = previousState.prescriberName;
          } else {
            delete updatedPrescriptionDetails.prescriberName;
          }
          if (previousState.refills !== undefined) {
            updatedPrescriptionDetails.refills = previousState.refills;
          } else {
            delete updatedPrescriptionDetails.refills;
          }
          if (previousState.prescribedQuantity !== undefined) {
            updatedPrescriptionDetails.prescribedQuantity = previousState.prescribedQuantity;
          } else {
            delete updatedPrescriptionDetails.prescribedQuantity;
          }
        }

        return {
          ...prevPayload,
          prescriptionDetails: isEmpty(updatedPrescriptionDetails)
            ? undefined
            : updatedPrescriptionDetails,
        };
      });
    }
  }, [prescriptionHistory, prescriptionHistoryIndex]);

  const redoPrescription = useCallback(() => {
    if (prescriptionHistoryIndex < prescriptionHistory.length - 1) {
      const nextIndex = prescriptionHistoryIndex + 1;
      const nextState = prescriptionHistory[nextIndex];
      setPrescriptionHistoryIndex(nextIndex);

      setPayload((prevPayload) => {
        const updatedPrescriptionDetails = { ...prevPayload?.prescriptionDetails };

        if (nextState.prescriberName !== undefined) {
          updatedPrescriptionDetails.prescriberName = nextState.prescriberName;
        } else {
          delete updatedPrescriptionDetails.prescriberName;
        }
        if (nextState.refills !== undefined) {
          updatedPrescriptionDetails.refills = nextState.refills;
        } else {
          delete updatedPrescriptionDetails.refills;
        }
        if (nextState.prescribedQuantity !== undefined) {
          updatedPrescriptionDetails.prescribedQuantity = nextState.prescribedQuantity;
        } else {
          delete updatedPrescriptionDetails.prescribedQuantity;
        }

        return {
          ...prevPayload,
          prescriptionDetails: isEmpty(updatedPrescriptionDetails)
            ? undefined
            : updatedPrescriptionDetails,
        };
      });
    }
  }, [prescriptionHistory, prescriptionHistoryIndex]);

  const undoFill = useCallback(() => {
    if (fillHistoryIndex >= 0) {
      const previousIndex = fillHistoryIndex - 1;
      let previousState = null;

      if (previousIndex < 0) {
        previousState = {};
        setFillHistoryIndex(-1);
      } else {
        previousState = fillHistory[previousIndex];
        setFillHistoryIndex(previousIndex);
      }

      setPayload((prevPayload) => {
        const updatedPrescriptionDetails = { ...prevPayload?.prescriptionDetails };

        if (!previousState || Object.keys(previousState).length === 0) {
          delete updatedPrescriptionDetails.dispensedQuantity;
        } else if (previousState.dispensedQuantity !== undefined) {
          updatedPrescriptionDetails.dispensedQuantity = previousState.dispensedQuantity;
        } else {
          delete updatedPrescriptionDetails.dispensedQuantity;
        }
        return {
          ...prevPayload,
          prescriptionDetails: isEmpty(updatedPrescriptionDetails)
            ? undefined
            : updatedPrescriptionDetails,
        };
      });
    }
  }, [fillHistory, fillHistoryIndex]);

  const redoFill = useCallback(() => {
    if (fillHistoryIndex < fillHistory.length - 1) {
      const nextIndex = fillHistoryIndex + 1;
      const nextState = fillHistory[nextIndex];
      setFillHistoryIndex(nextIndex);

      setPayload((prevPayload) => ({
        ...prevPayload,
        prescriptionDetails: {
          ...prevPayload?.prescriptionDetails,
          ...nextState,
        },
      }));
    }
  }, [fillHistory, fillHistoryIndex]);

  const canUndoPrescription = prescriptionHistoryIndex >= 0;
  const canRedoPrescription = prescriptionHistoryIndex < prescriptionHistory.length - 1;
  const canUndoFill = fillHistoryIndex >= 0;
  const canRedoFill = fillHistoryIndex < fillHistory.length - 1;

  const resetPrescriptionChanges = useCallback(() => {
    setPrescriptionHistory([]);
    setPrescriptionHistoryIndex(-1);
    setPayload((prevPayload) => {
      const updatedPrescriptionDetails = { ...prevPayload?.prescriptionDetails };
      delete updatedPrescriptionDetails.prescriberName;
      delete updatedPrescriptionDetails.refills;
      delete updatedPrescriptionDetails.prescribedQuantity;
      return {
        ...prevPayload,
        prescriptionDetails: isEmpty(updatedPrescriptionDetails)
          ? undefined
          : updatedPrescriptionDetails,
      };
    });
  }, []);

  const resetFillChanges = useCallback(() => {
    setFillHistory([]);
    setFillHistoryIndex(-1);
    setPayload((prevPayload) => {
      const updatedPrescriptionDetails = { ...prevPayload?.prescriptionDetails };
      delete updatedPrescriptionDetails.dispensedQuantity;
      return {
        ...prevPayload,
        prescriptionDetails: isEmpty(updatedPrescriptionDetails)
          ? undefined
          : updatedPrescriptionDetails,
      };
    });
  }, []);

  const clearHistory = useCallback(() => {
    setPrescriptionHistory([]);
    setPrescriptionHistoryIndex(-1);
    setFillHistory([]);
    setFillHistoryIndex(-1);
  }, []);

  const value = useMemo(
    () => ({
      payload,
      setPayload: updatePayload,
      savePrescriptionChanges,
      saveFillChanges,
      undoPrescription,
      redoPrescription,
      undoFill,
      redoFill,
      resetPrescriptionChanges,
      resetFillChanges,
      canUndoPrescription,
      canRedoPrescription,
      canUndoFill,
      canRedoFill,
      clearHistory,
    }),
    [
      payload,
      updatePayload,
      savePrescriptionChanges,
      saveFillChanges,
      undoPrescription,
      redoPrescription,
      undoFill,
      redoFill,
      resetPrescriptionChanges,
      resetFillChanges,
      canUndoPrescription,
      canRedoPrescription,
      canUndoFill,
      canRedoFill,
      clearHistory,
    ]
  );

  return (
    <PrescriptionReviewPayloadContext.Provider value={value}>
      {children}
    </PrescriptionReviewPayloadContext.Provider>
  );
};

PrescriptionReviewPayloadProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

export default PrescriptionReviewPayloadProvider;
